<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  useSlots,
  useTemplateRef,
  ref,
  getCurrentInstance,
  watch,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import MessageMeta from '../MessageMeta.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { formatMediaDuration } from 'dashboard/helper/mediaDuration';
import {
  audioPlaybackContentType,
  audioPlaybackSource,
} from 'dashboard/helper/audioAttachmentHelper';

const { attachment, appearance, showMessageMeta, compact } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
  appearance: {
    type: String,
    default: 'incoming',
    validator: value => ['incoming', 'outgoing', 'forwarded'].includes(value),
  },
  showMessageMeta: {
    type: Boolean,
    default: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

// Aceleres: a foto de quem gravou chega por slot (AudioPresentation). Onde ela
// nao existe -- lista de anexos, resultado de busca -- nao ha lugar para a
// pilula ir, entao a velocidade continua embaixo do waveform, como antes.
const slots = useSlots();
const hasAvatarSlot = computed(() =>
  Boolean(slots['audio-leading'] || slots['audio-trailing'])
);

const WAVEFORM_BAR_COUNT = 42;
const MIN_WAVEFORM_BAR_HEIGHT = 14;
const SEEK_INTERVAL_MS = 50;
const PLAYBACK_RATES = [1, 1.5, 2];
const waveformCache = new Map();

defineOptions({
  inheritAttrs: false,
});

// O endereco que se REPRODUZ nao e necessariamente `data_url` -- a regra e de
// audioPlaybackSource (audioAttachmentHelper).
const sourceURL = computed(() => audioPlaybackSource(attachment));
const timeStampURL = computed(() => {
  return timeStampAppendedURL(sourceURL.value);
});
const waveformURL = computed(() => {
  const separator = timeStampURL.value.includes('?') ? '&' : '?';
  return `${timeStampURL.value}${separator}acl_waveform=1`;
});
const playbackURL = ref(timeStampURL.value);

const audioPlayer = useTemplateRef('audioPlayer');
const waveformRef = useTemplateRef('waveformRef');

const isPlaying = ref(false);
const isPlaybackStarting = ref(false);
const isSeeking = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackRate = ref(1);
const waveformBars = ref(
  Array.from({ length: WAVEFORM_BAR_COUNT }, () => MIN_WAVEFORM_BAR_HEIGHT)
);

let waveformAbortController;
let waveformObjectURL;
let playbackAnimationFrame = null;
let seekAnimationFrame = null;
let pendingSeekTime = null;
let lastSeekCommitAt = 0;
let activeSourceURL = sourceURL.value;
let hasPendingSourceUpdate = false;

const { uid } = getCurrentInstance();

const updateDuration = () => {
  const audioDuration = audioPlayer.value?.duration;
  if (Number.isFinite(audioDuration) && audioDuration > 0) {
    duration.value = audioDuration;
  }
};

const useDecodedAudioForPlayback = (
  audioData,
  responseContentType,
  requestedSourceURL
) => {
  if (isPlaying.value || isPlaybackStarting.value) return;
  if (requestedSourceURL !== activeSourceURL) return;

  const contentType =
    responseContentType || audioPlaybackContentType(attachment) || 'audio/ogg';

  if (waveformObjectURL) URL.revokeObjectURL(waveformObjectURL);

  waveformObjectURL = URL.createObjectURL(
    new Blob([audioData], { type: contentType })
  );
  playbackURL.value = waveformObjectURL;
};

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

// Tempo exibido embaixo do waveform: enquanto não tocou nada, mostra a
// duração total (igual WhatsApp num áudio recebido). Quando dá play, mostra
// o tempo corrente.
const displayTime = computed(() => {
  if (isPlaying.value || currentTime.value > 0) return currentTime.value;
  return duration.value;
});

// Aceleres: "ja comecaram a ouvir". Enquanto for false a bolha mostra a foto
// de quem gravou; a partir do play ela da lugar ao seletor de velocidade,
// como no WhatsApp. Ao terminar, o onEnded zera currentTime e a foto volta.
const hasPlaybackStarted = computed(
  () => isPlaying.value || currentTime.value > 0
);

const waveformFromAudio = audioBuffer => {
  const samples = audioBuffer.getChannelData(0);
  const chunkSize = Math.ceil(samples.length / WAVEFORM_BAR_COUNT);
  const strengths = Array.from({ length: WAVEFORM_BAR_COUNT }, (_, index) => {
    const start = index * chunkSize;
    const end = Math.min(samples.length, start + chunkSize);
    const step = Math.max(1, Math.floor((end - start) / 200));
    let sum = 0;
    let count = 0;

    for (let sampleIndex = start; sampleIndex < end; sampleIndex += step) {
      sum += samples[sampleIndex] ** 2;
      count += 1;
    }

    return Math.sqrt(sum / Math.max(count, 1));
  });
  const peak = Math.max(...strengths, Number.EPSILON);

  return strengths.map(strength => {
    const normalized = Math.min(1, strength / peak);
    return Math.round(
      MIN_WAVEFORM_BAR_HEIGHT +
        Math.sqrt(normalized) * (100 - MIN_WAVEFORM_BAR_HEIGHT)
    );
  });
};

const loadWaveform = async () => {
  const requestedSourceURL = sourceURL.value;
  const requestedWaveformURL = waveformURL.value;
  const cachedWaveform = waveformCache.get(requestedSourceURL);
  if (cachedWaveform) {
    waveformBars.value = cachedWaveform;
    return;
  }

  waveformAbortController?.abort();
  const abortController = new AbortController();
  waveformAbortController = abortController;

  try {
    const response = await fetch(requestedWaveformURL, {
      credentials: 'same-origin',
      signal: abortController.signal,
    });
    if (!response.ok) return;

    const AudioContextConstructor =
      window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return;

    const audioContext = new AudioContextConstructor();
    try {
      const audioData = await response.arrayBuffer();
      useDecodedAudioForPlayback(
        audioData,
        response.headers.get('content-type'),
        requestedSourceURL
      );
      const audioBuffer = await audioContext.decodeAudioData(
        audioData.slice(0)
      );
      if (requestedSourceURL !== activeSourceURL) return;

      const bars = waveformFromAudio(audioBuffer);
      waveformCache.set(requestedSourceURL, bars);
      waveformBars.value = bars;
      if (Number.isFinite(audioBuffer.duration) && audioBuffer.duration > 0) {
        duration.value = audioBuffer.duration;
      }
    } finally {
      audioContext.close();
    }
  } catch {
    // O player nativo continua sendo usado se a origem não permitir análise.
  } finally {
    if (waveformAbortController === abortController) {
      waveformAbortController = null;
    }
  }
};

const applyLatestPlaybackSource = () => {
  const nextSourceURL = sourceURL.value;
  if (!nextSourceURL || nextSourceURL === activeSourceURL) return;

  // Um Android pode ja estar reproduzindo o Ogg/WebM quando a derivada chega.
  // Nesse caso a troca espera o fim para nao interromper o audio no meio.
  if (isPlaying.value) {
    hasPendingSourceUpdate = true;
    return;
  }

  waveformAbortController?.abort();
  if (waveformObjectURL) URL.revokeObjectURL(waveformObjectURL);
  waveformObjectURL = null;
  activeSourceURL = nextSourceURL;
  hasPendingSourceUpdate = false;
  playbackURL.value = timeStampURL.value;
  isPlaybackStarting.value = false;
  currentTime.value = 0;
  duration.value = 0;
  waveformBars.value = Array.from(
    { length: WAVEFORM_BAR_COUNT },
    () => MIN_WAVEFORM_BAR_HEIGHT
  );
  loadWaveform();
};

// `message.updated` entrega o playback_url M4A alguns segundos depois de a
// bolha nascer. Sem observar essa mudanca o componente conserva o Ogg/WebM
// inicial ate a pagina ser recarregada, formato que o iPhone nao reproduz.
watch(sourceURL, applyLatestPlaybackSource);

// Velocidade de reprodução (1x / 1,5x / 2x), no mesmo modelo do WhatsApp:
// um toque avança para a próxima velocidade e o áudio continua tocando.
const playbackRateLabel = computed(() =>
  String(playbackRate.value).replace('.', ',')
);

// O botao mostra "1,5x" e nao so "1,5": sem o `x` o numero sozinho nao diz que
// e velocidade. Fica separado do `playbackRateLabel` porque esse ja e usado
// dentro de frases que acrescentam o `x` (aria-label e title) — juntar os dois
// daria "1,5xx" na leitura do leitor de ecra.
const playbackRateText = computed(() => `${playbackRateLabel.value}x`);

const applyPlaybackRate = () => {
  const player = audioPlayer.value;
  if (!player) return;

  // Trocar o src (o blob decodificado para o waveform) recarrega o elemento e
  // o navegador restaura playbackRate a partir de defaultPlaybackRate, então
  // os dois precisam andar juntos.
  player.defaultPlaybackRate = playbackRate.value;
  if (player.playbackRate !== playbackRate.value) {
    player.playbackRate = playbackRate.value;
  }
};

onMounted(() => {
  updateDuration();
  applyPlaybackRate();
  loadWaveform();
});

onBeforeUnmount(() => {
  waveformAbortController?.abort();
  if (waveformObjectURL) URL.revokeObjectURL(waveformObjectURL);
  if (playbackAnimationFrame !== null) {
    cancelAnimationFrame(playbackAnimationFrame);
  }
  if (seekAnimationFrame !== null) {
    cancelAnimationFrame(seekAnimationFrame);
  }
});

// Quantos bars marcar como "já ouvidos" — proporcional ao progresso.
const progressIndex = computed(() => {
  if (!duration.value || !Number.isFinite(currentTime.value)) return 0;
  return Math.round((currentTime.value / duration.value) * WAVEFORM_BAR_COUNT);
});

// Posição da bolinha verde (em %) sobre o waveform. Quando não há
// duração ainda ou nada foi tocado, fica colada no início (0%).
const playheadPercent = computed(() => {
  if (!duration.value || !Number.isFinite(currentTime.value)) return 0;
  return Math.min(100, Math.max(0, (currentTime.value / duration.value) * 100));
});

const mobilePlayheadPercent = computed(() =>
  Math.min(100, Math.max(0, (progressIndex.value / WAVEFORM_BAR_COUNT) * 100))
);

const onTimeUpdate = () => {
  const playerTime = audioPlayer.value?.currentTime;
  if (!isSeeking.value && Number.isFinite(playerTime)) {
    currentTime.value = playerTime;
  }
};

const stopPlaybackAnimation = () => {
  if (playbackAnimationFrame === null) return;

  cancelAnimationFrame(playbackAnimationFrame);
  playbackAnimationFrame = null;
};

const updatePlaybackPosition = () => {
  if (!isPlaying.value || !audioPlayer.value) return;

  if (!isSeeking.value) {
    const playerTime = audioPlayer.value.currentTime;
    if (Number.isFinite(playerTime)) currentTime.value = playerTime;
  }
  playbackAnimationFrame = requestAnimationFrame(updatePlaybackPosition);
};

const startPlaybackAnimation = () => {
  stopPlaybackAnimation();
  updatePlaybackPosition();
};

const cyclePlaybackRate = () => {
  const currentIndex = PLAYBACK_RATES.indexOf(playbackRate.value);
  playbackRate.value =
    PLAYBACK_RATES[(currentIndex + 1) % PLAYBACK_RATES.length];
  applyPlaybackRate();
};

const commitPendingSeek = () => {
  if (pendingSeekTime === null || !audioPlayer.value) return;

  const seekTime = pendingSeekTime;
  audioPlayer.value.currentTime = seekTime;
  currentTime.value = seekTime;
  pendingSeekTime = null;
  lastSeekCommitAt = performance.now();
};

const flushPendingSeek = () => {
  if (seekAnimationFrame !== null) {
    cancelAnimationFrame(seekAnimationFrame);
    seekAnimationFrame = null;
  }

  commitPendingSeek();
};

const scheduleSeek = () => {
  if (seekAnimationFrame !== null) return;

  seekAnimationFrame = requestAnimationFrame(() => {
    seekAnimationFrame = null;
    if (
      isSeeking.value &&
      performance.now() - lastSeekCommitAt < SEEK_INTERVAL_MS
    ) {
      scheduleSeek();
      return;
    }

    commitPendingSeek();
  });
};

const seekToPosition = clientX => {
  if (!duration.value || !waveformRef.value || !audioPlayer.value) return;
  const rect = waveformRef.value.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const time = ratio * duration.value;
  pendingSeekTime = time;
  currentTime.value = time;
  scheduleSeek();
};

const startSeeking = event => {
  if (!duration.value) return;

  isSeeking.value = true;
  event.currentTarget.setPointerCapture(event.pointerId);
  seekToPosition(event.clientX);
};

const continueSeeking = event => {
  if (isSeeking.value) seekToPosition(event.clientX);
};

const stopSeeking = event => {
  if (!isSeeking.value) return;

  seekToPosition(event.clientX);
  flushPendingSeek();
  isSeeking.value = false;
  if (event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }
};

const onPlaying = () => {
  isPlaybackStarting.value = false;
  if (!isPlaying.value) {
    isPlaying.value = true;
    startPlaybackAnimation();
  }
};

const onPause = () => {
  isPlaybackStarting.value = false;
  isPlaying.value = false;
  stopPlaybackAnimation();
};

const onLoadedMetadata = () => {
  updateDuration();
  applyPlaybackRate();
};

const onCanPlay = () => {
  updateDuration();
  applyPlaybackRate();
};

const onPlaybackError = () => {
  isPlaybackStarting.value = false;
  isPlaying.value = false;
  stopPlaybackAnimation();
  if (hasPendingSourceUpdate) applyLatestPlaybackSource();
};

const playOrPause = async () => {
  const player = audioPlayer.value;
  if (!player) return;

  if (isPlaybackStarting.value) return;

  if (isPlaying.value) {
    player.pause();
    return;
  }

  emitter.emit('pause_playing_audio', uid);
  player.muted = false;
  player.volume = 1;
  applyPlaybackRate();
  isPlaybackStarting.value = true;

  try {
    await player.play();
  } catch {
    onPlaybackError();
  }
};

const onEnd = () => {
  isPlaybackStarting.value = false;
  isPlaying.value = false;
  currentTime.value = 0;
  stopPlaybackAnimation();
  if (hasPendingSourceUpdate) applyLatestPlaybackSource();
};
</script>

<template>
  <audio
    ref="audioPlayer"
    :src="playbackURL"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @durationchange="updateDuration"
    @canplay="onCanPlay"
    @timeupdate="onTimeUpdate"
    @playing="onPlaying"
    @pause="onPause"
    @error="onPlaybackError"
    @ended="onEnd"
  />
  <div
    v-bind="$attrs"
    class="acl-wa-audio rounded-xl w-full flex flex-col"
    :class="[
      `acl-wa-audio--${appearance}`,
      { 'acl-wa-audio--compact': compact },
    ]"
  >
    <slot name="audio-header" />
    <div class="acl-wa-audio__row">
      <slot v-if="!hasPlaybackStarted" name="audio-leading" />
      <button
        type="button"
        class="acl-wa-audio__play"
        :class="[
          isPlaying ? 'is-playing' : 'is-paused',
          { 'is-loading': isPlaybackStarting },
        ]"
        :aria-label="
          isPlaybackStarting
            ? 'Carregando áudio'
            : isPlaying
              ? 'Pausar áudio'
              : 'Reproduzir áudio'
        "
        @click="playOrPause"
      >
        <Icon
          v-if="isPlaying"
          class="size-6"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-6" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="acl-wa-audio__column translate-y-2">
        <div
          ref="waveformRef"
          class="acl-wa-audio__waveform"
          :class="{ 'is-seeking': isSeeking }"
          @pointerdown="startSeeking"
          @pointermove="continueSeeking"
          @pointerup="stopSeeking"
          @pointercancel="stopSeeking"
        >
          <div
            v-for="(h, i) in waveformBars"
            :key="i"
            class="acl-wa-audio__bar"
            :class="{ 'is-played': i < progressIndex }"
            :style="{ '--waveform-bar-height': h + '%' }"
          />
          <span
            class="acl-wa-audio__playhead acl-wa-audio__playhead--mobile"
            :style="{
              left: playheadPercent + '%',
              '--acl-mobile-waveform-playhead-left':
                mobilePlayheadPercent + '%',
            }"
            aria-hidden="true"
          />
        </div>
        <div class="acl-wa-audio__meta">
          <span class="tabular-nums">
            {{ formatMediaDuration(displayTime) }}
          </span>
          <button
            v-if="!hasAvatarSlot"
            type="button"
            class="acl-wa-audio__speed"
            :class="{ 'is-boosted': playbackRate !== 1 }"
            :aria-label="`Velocidade de reprodução: ${playbackRateLabel}x`"
            :title="`Velocidade de reprodução: ${playbackRateLabel}x`"
            @click.stop="cyclePlaybackRate"
          >
            {{ playbackRateText }}
          </button>
          <MessageMeta
            v-if="showMessageMeta"
            class="acl-wa-audio__messagemeta"
          />
        </div>
      </div>
      <slot v-if="!hasPlaybackStarted" name="audio-trailing" />
      <span
        v-if="hasAvatarSlot && hasPlaybackStarted"
        class="acl-wa-audio__speed-slot"
      >
        <button
          type="button"
          class="acl-wa-audio__speed acl-wa-audio__speed--avatar"
          :class="{ 'is-boosted': playbackRate !== 1 }"
          :aria-label="`Velocidade de reprodução: ${playbackRateLabel}x`"
          :title="`Velocidade de reprodução: ${playbackRateLabel}x`"
          @click.stop="cyclePlaybackRate"
        >
          {{ playbackRateText }}
        </button>
      </span>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 mt-2 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Espelha o bloco de mesmo nome em public/aceleres-overrides.css, que vence
// com !important. Os dois andam juntos: mudar só um deixa a medida divergente
// para quem for ler o componente depois.
.acl-wa-audio {
  width: 336px;
  min-width: 336px;
  max-width: 336px;
  padding: 9px 11px 8px;
}

.acl-wa-audio--forwarded {
  padding-top: 28px;
}

.acl-wa-audio--outgoing {
  background: transparent;
}

.acl-wa-audio.acl-wa-audio--compact {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 0;
}

.acl-wa-audio--compact .acl-wa-audio__row,
.acl-wa-audio--compact .acl-wa-audio__column {
  min-width: 0;
}

.acl-wa-audio__row {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  /* 50 + 9 + 8 de padding = os 67px de altura do cartao (pedido 02/09). */
  height: 50px;
}
/* Aceleres 2026-08-17: play um pouco maior (32 -> 36). Estas medidas existem
   tambem em public/aceleres-overrides.css, que vence por `!important` e e quem
   de fato pinta a bolha -- mudar so um dos dois faz o ajuste "nao acontecer".
   La os paddings de .acl-wa-audio__meta somam avatar + gap + play na mao. */
.acl-wa-audio__play {
  width: 36px;
  height: 36px;
  display: grid;
  place-content: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: inherit;
  flex-shrink: 0;
  border-radius: 50%;
  transition: background 0.12s;
}
.acl-wa-audio__play:hover {
  background: rgba(0, 0, 0, 0.06);
}

.acl-wa-audio__play.is-playing > *,
.acl-wa-audio__play.is-paused > * {
  display: none;
}

.acl-wa-audio__play.is-paused::before {
  content: '';
  width: 0;
  height: 0;
  margin-left: 2px;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 13px solid currentColor;
}

.acl-wa-audio__play.is-playing::before {
  content: '';
  width: 13px;
  height: 16px;
  background: linear-gradient(
    to right,
    currentColor 0 5px,
    transparent 5px 8px,
    currentColor 8px 13px
  );
}
.acl-wa-audio__column {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.acl-wa-audio__waveform {
  position: relative;
  width: 100%;
  min-width: 0;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  touch-action: none;
}
.acl-wa-audio__waveform.is-seeking {
  cursor: grabbing;
}
.acl-wa-audio__waveform.is-seeking .acl-wa-audio__playhead {
  transition: none;
}
.acl-wa-audio__bar {
  flex: 1 1 0;
  min-width: 2px;
  background: rgba(71, 85, 105, 0.25);
  border-radius: 2px;
  transition: background 0.08s linear;
}
.acl-wa-audio__bar.is-played {
  background: #53bdeb;
}
.acl-wa-audio__playhead {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  background: #53bdeb;
  border-radius: 50%;
  box-shadow: none;
  transform: translateY(-50%);
  pointer-events: none;
  /* Sem transicao de proposito: `updatePlaybackPosition` escreve `left` a cada
     frame do `requestAnimationFrame`, e uma transicao de 80ms reiniciava-se em
     cada escrita — o ponto ficava permanentemente atras da posicao real e a
     leitura era de "travar" por cima da onda. Medido em 02/09: 8px de atraso
     no fim de uma trilha de 185px. Gemeo `!important` em
     public/aceleres-overrides.css, que e quem vence. */
}
.acl-wa-audio__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  font-size: 11px;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.55);
  padding: 3px 0 0;
}
.acl-wa-audio__meta > .tabular-nums {
  flex-shrink: 0;
}
.acl-wa-audio__messagemeta {
  font-size: 13px;
  line-height: 1.2;
  color: inherit;
}

:deep(.dark) .acl-wa-audio__play:hover {
  background: rgba(255, 255, 255, 0.08);
}
:deep(.dark) .acl-wa-audio__bar {
  background: rgba(255, 255, 255, 0.28);
}
:deep(.dark) .acl-wa-audio__bar.is-played {
  background: rgba(255, 255, 255, 0.55);
}
:deep(.dark) .acl-wa-audio__playhead {
  box-shadow: none;
}
:deep(.dark) .acl-wa-audio__meta {
  color: rgba(255, 255, 255, 0.55);
}

.acl-wa-audio--outgoing .acl-wa-audio__bar {
  background: rgba(74, 124, 86, 0.28);
}

.acl-wa-audio__speed {
  flex-shrink: 0;
  min-width: 40px;
  height: 20px;
  padding: 0 9px;
  display: grid;
  place-content: center;
  border: 0;
  border-radius: 9999px;
  background: rgba(71, 85, 105, 0.16);
  color: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition:
    background 0.12s,
    color 0.12s;
}
.acl-wa-audio__speed:hover {
  background: rgba(71, 85, 105, 0.26);
}
/* O `margin-right: auto` era do botao ENQUANTO ele morava na linha do tempo:
   e ele que empurra o horario para a direita ali. Fica preso a esse contexto
   para nao agir na pilula que agora ocupa o lugar da foto -- la ela e o ultimo
   item da linha, e uma margem automatica a descolaria da borda. */
.acl-wa-audio__meta > .acl-wa-audio__speed {
  margin-right: auto;
}
.acl-wa-audio__speed.is-boosted {
  background: rgba(71, 85, 105, 0.34);
  font-weight: 700;
}
.acl-wa-audio__speed.is-boosted:hover {
  background: rgba(71, 85, 105, 0.44);
}
.acl-wa-audio--compact .acl-wa-audio__speed {
  min-width: 38px;
  padding: 0 7px;
}

/* Aceleres: a caixa que substitui a foto tem a MESMA largura E a MESMA ALTURA
   dela (50px nos dois sentidos desde 02/09 -- ver .aceleres-audio-avatar-wrap
   em aceleres-overrides.css). Sem repetir a caixa inteira, a bolha encolhia no
   instante do play, porque a pilula sozinha tem 26px de altura. A pilula fica
   centrada dentro dessa caixa. */
.acl-wa-audio__speed-slot {
  display: flex;
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
  align-items: center;
  justify-content: center;
}
.acl-wa-audio__speed--avatar {
  width: 100%;
  height: 26px;
  padding: 0;
  background: rgba(71, 85, 105, 0.62);
  color: #fff;
  font-size: 12px;
}
.acl-wa-audio__speed--avatar:hover {
  background: rgba(71, 85, 105, 0.78);
}
.acl-wa-audio__speed--avatar.is-boosted,
.acl-wa-audio__speed--avatar.is-boosted:hover {
  background: rgba(71, 85, 105, 0.88);
}

:deep(.dark) .acl-wa-audio__speed {
  background: rgba(255, 255, 255, 0.14);
}
:deep(.dark) .acl-wa-audio__speed:hover {
  background: rgba(255, 255, 255, 0.24);
}
:deep(.dark) .acl-wa-audio__speed.is-boosted {
  background: rgba(255, 255, 255, 0.32);
}
:deep(.dark) .acl-wa-audio__speed.is-boosted:hover {
  background: rgba(255, 255, 255, 0.42);
}

/* Mantém a onda visível mesmo com as regras globais da personalização. */
.acl-wa-audio.acl-wa-audio.acl-wa-audio .acl-wa-audio__bar {
  height: var(--waveform-bar-height) !important;
}

@media (max-width: 767px) {
  .acl-wa-audio__playhead--mobile {
    left: var(--acl-mobile-waveform-playhead-left) !important;
  }
}
</style>
